import { db } from './schema';

const VERSION = 2;

async function toBytes(blob) {
  return new Uint8Array(await blob.arrayBuffer());
}

export async function exportBackup() {
  const [{ zipSync, strToU8 }, jobs, blobs, app, clients, settingsRows, postcodeCache] =
    await Promise.all([
      import('fflate'),
      db.jobs.toArray(),
      db.blobs.toArray(),
      db.app.toArray(),
      db.clients.toArray(),
      db.settings.toArray(),
      db.postcodeCache.toArray()
    ]);

  const files = {
    'manifest.json': strToU8(
      JSON.stringify({ schema: 'sign-off-backup', version: VERSION })
    ),
    'jobs.json': strToU8(JSON.stringify(jobs)),
    'app.json': strToU8(JSON.stringify(app)),
    'clients.json': strToU8(JSON.stringify(clients)),
    // The logo blob would bloat the archive as JSON; settings rows keep only
    // text settings. Logo is intentionally excluded (re-add it on the new device).
    'settings.json': strToU8(
      JSON.stringify(
        settingsRows.filter((row) => row.key !== 'logo').map((row) => ({
          key: row.key,
          value: row.value,
          updatedAt: row.updatedAt
        }))
      )
    ),
    'postcodeCache.json': strToU8(
      JSON.stringify(
        postcodeCache.map((row) => ({
          postcode: row.postcode,
          cachedAt: row.cachedAt,
          results: row.results
        }))
      )
    )
  };

  for (const row of blobs) {
    const metadata = {
      id: row.id,
      images: row.images.map(({ width, height, caption, capturedAt, gps }, index) => ({
        index,
        width,
        height,
        caption: caption || null,
        capturedAt: capturedAt || null,
        gps: gps || null
      }))
    };

    files[`blobs/${row.id}/metadata.json`] = strToU8(JSON.stringify(metadata));

    for (const [index, image] of row.images.entries()) {
      files[`blobs/${row.id}/image-${index}.jpg`] = await toBytes(image.image);
      files[`blobs/${row.id}/thumb-${index}.jpg`] = await toBytes(image.thumbnail);
    }

    if (row.signature) {
      files[`blobs/${row.id}/signature.png`] = await toBytes(row.signature);
    }

    if (row.pdf) {
      files[`blobs/${row.id}/proof.pdf`] = await toBytes(row.pdf);
    }
  }

  return new Blob([zipSync(files, { level: 6 })], { type: 'application/zip' });
}

export async function importBackup(file) {
  const { unzipSync, strFromU8 } = await import('fflate');
  const files = unzipSync(new Uint8Array(await file.arrayBuffer()));

  if (!files['manifest.json'] || !files['jobs.json']) {
    throw new Error('This is not a valid Sign-off backup.');
  }

  const manifest = JSON.parse(strFromU8(files['manifest.json']));

  if (manifest.schema !== 'sign-off-backup') {
    throw new Error('This backup version is not supported.');
  }

  if (manifest.version !== VERSION) {
    throw new Error(
      `This backup is version ${manifest.version}; this app reads version ${VERSION}. Update the app, or export a fresh backup.`
    );
  }

  const jobs = JSON.parse(strFromU8(files['jobs.json']));

  if (!Array.isArray(jobs) || jobs.some((job) => !job.id || !job.createdAt || !job.title)) {
    throw new Error('The backup contains invalid record metadata.');
  }

  await db.transaction('rw', db.jobs, db.blobs, async () => {
    for (const job of jobs) {
      if (await db.jobs.get(job.id)) continue;

      const path = `blobs/${job.id}/`;
      const metadataFile = files[`${path}metadata.json`];

      if (!metadataFile) {
        throw new Error(`Backup data is missing for record ${job.id}.`);
      }

      const metadata = JSON.parse(strFromU8(metadataFile));
      const images = metadata.images.map(
        ({ index, width, height, caption, capturedAt, gps }) => {
          const image = files[`${path}image-${index}.jpg`];
          const thumbnail = files[`${path}thumb-${index}.jpg`];

          if (!image || !thumbnail) {
            throw new Error(`A photo is missing from record ${job.id}.`);
          }

          return {
            image: new Blob([image], { type: 'image/jpeg' }),
            thumbnail: new Blob([thumbnail], { type: 'image/jpeg' }),
            width,
            height,
            caption: caption || null,
            capturedAt: capturedAt || null,
            gps: gps || null
          };
        }
      );

      const signature = files[`${path}signature.png`];
      const pdf = files[`${path}proof.pdf`];

      await db.jobs.add(job);
      await db.blobs.add({
        id: job.id,
        images,
        signature: signature
          ? new Blob([signature], { type: 'image/png' })
          : null,
        pdf: pdf ? new Blob([pdf], { type: 'application/pdf' }) : null
      });
    }
  });

  // Clients: merge-by-name, skip existing.
  if (files['clients.json']) {
    const clients = JSON.parse(strFromU8(files['clients.json']));

    for (const client of clients || []) {
      if (!client?.name) continue;

      const existing = await db.clients.where('name').equals(client.name).first();

      if (!existing) {
        await db.clients.add({
          name: client.name,
          updatedAt: client.updatedAt || Date.now()
        });
      }
    }
  }

  // Settings: imported settings override local ones (logo row excluded).
  if (files['settings.json']) {
    const rows = JSON.parse(strFromU8(files['settings.json']));

    for (const row of rows || []) {
      if (row?.key && row.key !== 'logo') {
        await db.settings.put({
          key: row.key,
          value: row.value,
          updatedAt: row.updatedAt || Date.now()
        });
      }
    }
  }

  // Address cache: keep local (newer) entries, add missing.
  if (files['postcodeCache.json']) {
    const rows = JSON.parse(strFromU8(files['postcodeCache.json']));

    for (const row of rows || []) {
      if (!row?.postcode) continue;

      if (!(await db.postcodeCache.get(row.postcode))) {
        await db.postcodeCache.put({
          postcode: row.postcode,
          cachedAt: row.cachedAt || Date.now(),
          results: row.results || []
        });
      }
    }
  }
}
